import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import { ForwardToInbox, Block } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import {
  listInvites,
  sendInvite,
  resendInvite,
  revokeInvite,
} from "../../api/onboarding";
import { getRolesList } from "../../api/roles";
import { getTenantsList } from "../../api/tenant";
import useAuth from "../../hooks/useAuth";
import { useNotify } from "../../utils/useNotify";
import { formatAxiosError } from "../../utils/apiError";
import { APP_HOME_PATH } from "../../config/routes";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard, {
  MobileListCardSkeleton,
} from "../../components/MobileListCard";

function sortRolesForInvite(rows) {
  const preset = ["Staff", "Manager", "Bookkeeper"];
  return [...rows].sort((a, b) => {
    if (a.name === "TenantAdmin") return 1;
    if (b.name === "TenantAdmin") return -1;
    const ia = preset.indexOf(a.name);
    const ib = preset.indexOf(b.name);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.name.localeCompare(b.name);
  });
}

function defaultRoleId(sortedTenantRoles) {
  const staff = sortedTenantRoles.find((x) => x.name === "Staff");
  const nonAdmin =
    sortedTenantRoles.find((x) => x.name !== "TenantAdmin") ||
    sortedTenantRoles[0];
  const pick = staff || nonAdmin;
  return pick ? String(pick.id) : "";
}

/** @param {object} row invitation row from API */
function registrationSummary(row, tenantId) {
  if (row.state === "accepted" || row.user_joined_this_tenant) {
    return "Joined this organization";
  }
  if (row.user_account_pending_stub) {
    return "Account exists — invite not completed";
  }
  if (row.user_exists && row.user_tenant_id != null && row.user_tenant_id !== tenantId) {
    return "User linked to another organization";
  }
  if (row.user_exists) {
    return "Account exists";
  }
  return "No account yet";
}

function InviteCard({
  row,
  effectiveTenantId,
  inviteTenantIdParam,
  inviteActionBusy,
  onResend,
  onRevoke,
}) {
  const canAct = row.state === "pending" || row.state === "expired";
  return (
    <MobileListCard
      title={row.email}
      subtitle={row.role_name}
      status={
        <Chip
          label={row.state}
          size="small"
          sx={{ textTransform: "capitalize" }}
        />
      }
      fields={[
        {
          label: "Expires",
          value: row.expires_at
            ? new Date(row.expires_at).toLocaleString()
            : "—",
        },
        {
          label: "Registration",
          value: registrationSummary(row, effectiveTenantId),
        },
      ]}
      actions={
        canAct ? (
          <>
            <Tooltip title="Resend invitation email (new link and expiry)">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Resend invitation"
                  disabled={
                    inviteActionBusy === `${row.id}-resend` ||
                    inviteActionBusy === `${row.id}-revoke`
                  }
                  onClick={() => onResend(row)}
                >
                  {inviteActionBusy === `${row.id}-resend` ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ForwardToInbox fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Revoke this invitation (link will stop working)">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  aria-label="Revoke invitation"
                  disabled={
                    inviteActionBusy === `${row.id}-resend` ||
                    inviteActionBusy === `${row.id}-revoke`
                  }
                  onClick={() => onRevoke(row)}
                >
                  {inviteActionBusy === `${row.id}-revoke` ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Block fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : null
      }
    />
  );
}

export default function AdminInvites() {
  const { user } = useAuth();
  const notify = useNotify();
  const isMobile = useIsMobileList("md");
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteTab, setInviteTab] = useState("pending");
  const [inviteActionBusy, setInviteActionBusy] = useState(null);
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendLoading, setSendLoading] = useState(false);
  const [roleInit, setRoleInit] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(null);

  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );

  const effectiveTenantId = isSuperAdmin
    ? tenantFilter?.id ?? null
    : user?.tenant_id ?? null;

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await getTenantsList({ for_filter: true });
        if (!cancelled) setTenants(Array.isArray(raw) ? raw : []);
      } catch {
        if (!cancelled) setTenants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  const tenantRoles = useMemo(() => {
    if (effectiveTenantId == null) return [];
    return sortRolesForInvite(
      roles.filter((r) => r.tenant_id === effectiveTenantId)
    );
  }, [roles, effectiveTenantId]);

  const inviteTenantIdParam = isSuperAdmin ? effectiveTenantId : undefined;

  const load = useCallback(async () => {
    if (effectiveTenantId == null) {
      setRoles([]);
      setInvites([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [r, p] = await Promise.all([
        getRolesList({
          skip: 0,
          limit: 200,
          tenant_id: isSuperAdmin ? effectiveTenantId : undefined,
        }),
        listInvites({
          tenant_id: inviteTenantIdParam,
          status: inviteTab,
          limit: 100,
        }),
      ]);
      setRoles(Array.isArray(r) ? r : []);
      setInvites(Array.isArray(p) ? p : []);
      setRoleInit(false);
    } catch (e) {
      setError(formatAxiosError(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, isSuperAdmin, inviteTenantIdParam, inviteTab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (roleInit || loading || !tenantRoles.length) return;
    setRoleId(defaultRoleId(tenantRoles));
    setRoleInit(true);
  }, [tenantRoles, loading, roleInit]);

  const selectedRole = tenantRoles.find((r) => String(r.id) === roleId);
  const farmName =
    (isSuperAdmin ? tenantFilter?.name : user?.profile?.tenant_name) ||
    "your farm";
  const invitingAsAdmin = selectedRole?.name === "TenantAdmin";

  const onSend = async (e) => {
    e.preventDefault();
    setError("");
    setSendLoading(true);
    try {
      const payload = { email, role_id: Number(roleId) };
      if (inviteTenantIdParam != null) {
        payload.tenant_id = inviteTenantIdParam;
      }
      await sendInvite(payload);
      notify.success("Invitation sent.");
      setEmail("");
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not send invitation";
      setError(msg);
      notify.error(typeof msg === "string" ? msg : "Could not send invitation");
    } finally {
      setSendLoading(false);
    }
  };

  if (isSuperAdmin && tenantFilter == null) {
    return (
      <Box p={3}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Typography color="text.primary">Invitations</Typography>
        </Breadcrumbs>
        <Typography variant="h5" gutterBottom>
          Team invitations
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization to view invitations and send new ones.
        </Alert>
        <Box sx={{ maxWidth: 480, mb: 2 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={(_, v) => setTenantFilter(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization"
                placeholder="Select organization"
                helperText="Platform admins work with invitations per farm, like roles."
              />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      </Box>
    );
  }

  if (!isSuperAdmin && effectiveTenantId == null) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Invitations are managed per organization. Sign in as a farm admin.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Typography color="text.primary">Invitations</Typography>
      </Breadcrumbs>

      <Typography variant="h5" gutterBottom>
        Team invitations
      </Typography>

      {isSuperAdmin && (
        <Box sx={{ maxWidth: 480, mb: 2 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={(_, v) => setTenantFilter(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization"
                placeholder="Change organization"
                helperText="Managing invitations for the selected farm."
              />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === "string" ? error : JSON.stringify(error)}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Registration status</strong> is tracked from invitations (accepted
        date and role assignment), not the generic &quot;active&quot; flag on a user
        profile. A user row may exist before someone finishes accepting an invite
        (for example a placeholder from tenant setup).
      </Alert>
      {invitingAsAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are inviting someone as <strong>TenantAdmin</strong> (full access
          to settings, team, and roles). Prefer <strong>Staff</strong> or{" "}
          <strong>Manager</strong> unless they should run the farm account.
        </Alert>
      )}
      <Box component="form" onSubmit={onSend} sx={{ mb: 4, maxWidth: 480 }}>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          select
          label="Role"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          fullWidth
          margin="normal"
          required
        >
          {tenantRoles.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
        {selectedRole && email.trim() && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            They will join <strong>{farmName}</strong> as{" "}
            <strong>{selectedRole.name}</strong>. They must sign up or sign in
            with this email address.
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={sendLoading || !tenantRoles.length}
          startIcon={
            sendLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          {sendLoading ? "Sending…" : "Send invite"}
        </Button>
      </Box>
      <Typography variant="h6" gutterBottom>
        Invitations
      </Typography>
      <Tabs
        value={inviteTab}
        onChange={(_, v) => setInviteTab(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab label="Pending" value="pending" />
        <Tab label="Expired" value="expired" />
        <Tab label="Completed" value="accepted" />
        <Tab label="Revoked" value="revoked" />
        <Tab label="All" value="all" />
      </Tabs>
      {loading ? (
        isMobile ? (
          <Stack spacing={1.5}>
            {Array.from({ length: 3 }).map((_, i) => (
              <MobileListCardSkeleton key={i} />
            ))}
          </Stack>
        ) : (
          <Typography>Loading…</Typography>
        )
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {invites.length === 0 ? (
            <Typography color="text.secondary">
              No invitations in this view.
              {inviteTab === "expired" &&
                " Use Resend to issue a new link with a fresh expiry."}
            </Typography>
          ) : (
            invites.map((row) => (
              <InviteCard
                key={row.id}
                row={row}
                effectiveTenantId={effectiveTenantId}
                inviteTenantIdParam={inviteTenantIdParam}
                inviteActionBusy={inviteActionBusy}
                onResend={async (row) => {
                  setInviteActionBusy(`${row.id}-resend`);
                  try {
                    await resendInvite(row.id, {
                      tenant_id: inviteTenantIdParam,
                    });
                    notify.success("Invitation resent.");
                    await load();
                  } catch (err) {
                    const msg =
                      err?.response?.data?.detail ||
                      err?.message ||
                      "Could not resend invitation";
                    notify.error(
                      typeof msg === "string"
                        ? msg
                        : "Could not resend invitation"
                    );
                  } finally {
                    setInviteActionBusy(null);
                  }
                }}
                onRevoke={async (row) => {
                  setInviteActionBusy(`${row.id}-revoke`);
                  try {
                    await revokeInvite(row.id, {
                      tenant_id: inviteTenantIdParam,
                    });
                    notify.success("Invitation revoked.");
                    await load();
                  } catch (err) {
                    const msg =
                      err?.response?.data?.detail ||
                      err?.message ||
                      "Could not revoke invitation";
                    notify.error(
                      typeof msg === "string"
                        ? msg
                        : "Could not revoke invitation"
                    );
                  } finally {
                    setInviteActionBusy(null);
                  }
                }}
              />
            ))
          )}
        </Stack>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>
                <Tooltip title="Derived from invitation completion and user email match, not the generic profile active flag.">
                  <span>Registration</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.map((row) => {
              const canAct =
                row.state === "pending" || row.state === "expired";
              return (
                <TableRow key={row.id}>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.role_name}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>
                    {row.state}
                  </TableCell>
                  <TableCell>
                    {row.expires_at
                      ? new Date(row.expires_at).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {registrationSummary(row, effectiveTenantId)}
                  </TableCell>
                  <TableCell align="right">
                    {canAct ? (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Tooltip title="Resend invitation email (new link and expiry)">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="Resend invitation"
                              disabled={
                                inviteActionBusy === `${row.id}-resend` ||
                                inviteActionBusy === `${row.id}-revoke`
                              }
                              onClick={async () => {
                                setInviteActionBusy(`${row.id}-resend`);
                                try {
                                  await resendInvite(row.id, {
                                    tenant_id: inviteTenantIdParam,
                                  });
                                  notify.success("Invitation resent.");
                                  await load();
                                } catch (err) {
                                  const msg =
                                    err?.response?.data?.detail ||
                                    err?.message ||
                                    "Could not resend invitation";
                                  notify.error(
                                    typeof msg === "string"
                                      ? msg
                                      : "Could not resend invitation"
                                  );
                                } finally {
                                  setInviteActionBusy(null);
                                }
                              }}
                            >
                              {inviteActionBusy === `${row.id}-resend` ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <ForwardToInbox fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revoke this invitation (link will stop working)">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              aria-label="Revoke invitation"
                              disabled={
                                inviteActionBusy === `${row.id}-resend` ||
                                inviteActionBusy === `${row.id}-revoke`
                              }
                              onClick={async () => {
                                setInviteActionBusy(`${row.id}-revoke`);
                                try {
                                  await revokeInvite(row.id, {
                                    tenant_id: inviteTenantIdParam,
                                  });
                                  notify.success("Invitation revoked.");
                                  await load();
                                } catch (err) {
                                  const msg =
                                    err?.response?.data?.detail ||
                                    err?.message ||
                                    "Could not revoke invitation";
                                  notify.error(
                                    typeof msg === "string"
                                      ? msg
                                      : "Could not revoke invitation"
                                  );
                                } finally {
                                  setInviteActionBusy(null);
                                }
                              }}
                            >
                              {inviteActionBusy === `${row.id}-revoke` ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <Block fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {invites.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  No invitations in this view.
                  {inviteTab === "expired" &&
                    " Use Resend to issue a new link with a fresh expiry."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
